import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL, fmtEur } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  FilePdf, FileXls, FileZip, Receipt, Scales, Coins, ArrowDown, ArrowUp, Sparkle,
  Buildings, ArrowRight, MapPin, Wallet, Users as UsersIcon, ChartLineUp,
  Storefront, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const quarters = [{value:1,label:"1T (Ene-Mar)"},{value:2,label:"2T (Abr-Jun)"},{value:3,label:"3T (Jul-Sep)"},{value:4,label:"4T (Oct-Dic)"}];

function downloadAuthed(path, filename) {
  fetch(`${BACKEND_URL}${path}`, { credentials: "include" })
    .then((r)=>r.blob()).then((b)=>{
      const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = filename; a.click();
    }).catch(()=>toast.error("No se pudo descargar"));
}

/**
 * Fase 2D · GestorHome multi-org consolidado.
 * Backend endpoints reutilizados:
 *   GET  /api/me/orgs               → lista de orgs (also_org_ids)
 *   POST /api/auth/switch-org       → cambia contexto (cookie ag_active_org)
 *   GET  /api/dashboard/stats       → KPIs de la org activa (para agregado)
 *   GET  /api/tax/m303|m111|...     → exportación AEAT trimestral
 */
export default function GestorHome() {
  const now = new Date();
  const cq = Math.floor(now.getMonth()/3) + 1;
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(cq);
  const [stats, setStats] = useState(null);
  const [m303, setM303] = useState(null);
  const [m111, setM111] = useState(null);
  const [orgs, setOrgs] = useState(null);      // Fase 2D · /me/orgs
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [aggStats, setAggStats] = useState(null);  // Fase 2D · KPIs agregados multi-org
  const [aggLoading, setAggLoading] = useState(false);

  const load = async () => {
    try {
      const [s, a, b] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get(`/tax/m303?year=${year}&quarter=${quarter}`),
        api.get(`/tax/m111?year=${year}&quarter=${quarter}`),
      ]);
      setStats(s.data); setM303(a.data); setM111(b.data);
    } catch (e) { toast.error("Error cargando datos"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, quarter]);

  // Fase 2D · Cargar orgs multi-centro
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/orgs");
        setOrgs(data.items || []);
        setActiveOrgId(data.active_org_id || null);
      } catch { setOrgs([]); }
    })();
  }, []);

  // Fase 2D · Agregación de KPIs multi-org (secuencial, min invasión backend)
  const loadAggregate = async () => {
    if (!orgs || orgs.length < 2 || aggLoading) return;
    setAggLoading(true);
    try {
      const results = [];
      for (const o of orgs) {
        try {
          // Switch temporal + fetch stats. NOTE: si esto reload el shell, cambiar approach.
          // Aproach conservador: solo agregar la org activa por ahora + orgs listadas.
          if (o.id === activeOrgId) {
            const { data } = await api.get("/dashboard/stats");
            results.push({ org: o, stats: data, loaded: true });
          } else {
            results.push({ org: o, stats: null, loaded: false });
          }
        } catch { results.push({ org: o, stats: null, loaded: false }); }
      }
      setAggStats(results);
    } finally { setAggLoading(false); }
  };

  useEffect(() => {
    if (orgs && activeOrgId && orgs.length > 0) loadAggregate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgs, activeOrgId]);

  const activeOrg = useMemo(
    () => (orgs || []).find(o => o.id === activeOrgId) || (orgs || [])[0],
    [orgs, activeOrgId]
  );

  const isMultiOrg = orgs && orgs.length > 1;

  return (
    <div className="space-y-8">
      {/* ─── Fase 2D · Hero contextual multi-org ─── */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">
            {isMultiOrg ? "Vista general · Gestor multi-centro" : "Gestor · Asesoría"}
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">
            {isMultiOrg ? "Mis centros" : "Resumen contable"}
          </h1>
          <p className="text-sm text-[var(--ag-text-dim)] mt-2 max-w-xl">
            {isMultiOrg
              ? "Supervisa el rendimiento de los centros a tu cargo y exporta cada trimestre para Hacienda con un clic."
              : "Vista de solo lectura sobre el cierre del centro. Exporta el trimestre completo en un clic."
            }
          </p>
        </div>
        {activeOrg && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Centro activo</div>
            <div className="font-display font-bold text-lg tracking-tight text-[var(--ag-gold)]" data-testid="gestor-active-org-name">
              {activeOrg.name}
            </div>
            {activeOrg.city && (
              <div className="text-[11px] text-[var(--ag-text-dim)] inline-flex items-center gap-1">
                <MapPin size={10} /> {activeOrg.city}
              </div>
            )}
          </div>
        )}
      </header>

      {/* ─── Fase 2D · Mis centros (solo multi-org) ─── */}
      {isMultiOrg && (
        <GestorOrgsGrid
          orgs={orgs}
          activeOrgId={activeOrgId}
          aggStats={aggStats}
          onSwitchOrg={async (id) => {
            try {
              await api.post("/auth/switch-org", { org_id: id });
              window.location.reload();
            } catch { toast.error("No se pudo cambiar de centro"); }
          }}
        />
      )}

      {/* ─── Empty state: gestor sin centros aún ─── */}
      {orgs && orgs.length === 0 && (
        <div className="ag-card p-8 text-center border-[var(--ag-gold)]/30 bg-gradient-to-br from-[var(--ag-gold)]/5 to-transparent" data-testid="gestor-empty-orgs">
          <Buildings size={40} weight="duotone" className="text-[var(--ag-gold)] mx-auto" />
          <h2 className="font-display text-2xl font-bold tracking-tight mt-3">
            Aún no gestionas ningún centro
          </h2>
          <p className="text-sm text-[var(--ag-text-dim)] mt-2 max-w-md mx-auto">
            Pide a un cliente que canjee tu código de gestor desde su panel para empezar a supervisar su centro.
          </p>
          <Link
            to="/my-settings?tab=gestor-codes"
            className="inline-flex items-center gap-1.5 mt-4 px-5 py-2.5 rounded ag-btn-gold text-xs font-bold"
            data-testid="gestor-empty-cta"
          >
            <Sparkle size={12} weight="fill" /> Generar código de gestor <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      )}

      {/* Resumen mes actual (centro activo) */}
      {stats && (
        <section data-testid="gestor-active-kpis">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-3">
            {isMultiOrg ? `Este mes · ${activeOrg?.name || ""}` : "Este mes"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KpiCard label="Ingresos del mes" value={fmtEur(stats.total_ingresos)} sub={`${stats.services_count} servicios`} tone="aqua" testid="gestor-kpi-ingresos" />
            <KpiCard label="IVA neto" value={fmtEur(stats.iva_neto)} sub={`Rep ${fmtEur(stats.total_iva_repercutido)} · Sop ${fmtEur(stats.total_iva_soportado)}`} testid="gestor-kpi-iva" />
            <KpiCard label="Retenciones IRPF" value={fmtEur(stats.total_retenciones)} sub="Colaboradores + alquiler" tone="gold" testid="gestor-kpi-retenciones" />
            <KpiCard label="Hucha impuestos" value={fmtEur(stats.hucha_impuestos)} sub="Apartado para AEAT" tone="amber" testid="gestor-kpi-hucha" />
          </div>
        </section>
      )}

      {/* Exportación trimestral — protagonista */}
      <div className="ag-card p-8 bg-gradient-to-br from-[var(--ag-midnight-card)] to-[var(--ag-midnight-soft)] border-[var(--ag-gold)]/40">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
          <Sparkle size={12} weight="fill" /> Exportar Trimestre AEAT{activeOrg ? ` · ${activeOrg.name}` : ""}
        </div>
        <h2 className="font-display text-3xl font-black tracking-tighter mt-2">Descarga lista para Hacienda</h2>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">Modelo 303, Modelo 111, servicios y gastos del periodo. Listo para importar o adjuntar a la presentación AEAT.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Año</Label>
            <Input type="number" value={year} onChange={(e)=>setYear(Number(e.target.value))} className="mt-1 tabular-nums" data-testid="gestor-year" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Trimestre</Label>
            <Select value={String(quarter)} onValueChange={(v)=>setQuarter(Number(v))}>
              <SelectTrigger className="mt-1" data-testid="gestor-quarter"><SelectValue /></SelectTrigger>
              <SelectContent>{quarters.map((q)=> <SelectItem key={q.value} value={String(q.value)}>{q.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <div className="w-full text-center bg-[var(--ag-midnight)] border border-[var(--ag-border)] p-3 rounded text-xs text-[var(--ag-text-dim)]">
              {m303 && m111 ? (
                <>M303 <span className="font-display font-bold text-[var(--ag-gold)] tabular-nums">{fmtEur(m303.resultado)}</span> · M111 <span className="font-display font-bold text-[var(--ag-aqua-soft)] tabular-nums">{fmtEur(m111.total_a_ingresar)}</span></>
              ) : "Cargando…"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <button
            onClick={()=>downloadAuthed(`/api/tax/export-quarter/xlsx?year=${year}&quarter=${quarter}`, `AEAT-${year}-T${quarter}.xlsx`)}
            className="ag-btn-gold h-16 px-5 flex items-center gap-3 text-left rounded-md transition-all hover:scale-[1.02]"
            data-testid="gestor-export-xlsx"
          >
            <FileXls size={32} weight="duotone" />
            <div>
              <div className="font-display font-bold text-base leading-tight">Exportar XLSX</div>
              <div className="text-[11px] opacity-80">Excel único · 3 hojas (Resumen · Servicios · Gastos)</div>
            </div>
          </button>
          <button
            onClick={()=>downloadAuthed(`/api/tax/export-quarter/zip?year=${year}&quarter=${quarter}`, `AEAT-${year}-T${quarter}.zip`)}
            className="ag-btn-aqua h-16 px-5 flex items-center gap-3 text-left rounded-md transition-all hover:scale-[1.02]"
            data-testid="gestor-export-zip"
          >
            <FileZip size={32} weight="duotone" />
            <div>
              <div className="font-display font-bold text-base leading-tight">Exportar ZIP</div>
              <div className="text-[11px] opacity-90">CSVs separados · M303 · M111 · servicios · gastos</div>
            </div>
          </button>
        </div>
      </div>

      {/* Cuadros M303 / M111 lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {m303 && (
          <div className="ag-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-aqua-soft)] flex items-center gap-1.5"><Scales size={12} /> Modelo 303 · IVA</div>
                <h3 className="font-display font-bold text-2xl tracking-tighter mt-1">Trimestre {m303.period_label}</h3>
              </div>
              <Button variant="outline" className="border-[var(--ag-border)] bg-transparent" onClick={()=>downloadAuthed(`/api/tax/m303/pdf?year=${year}&quarter=${quarter}`, `m303-${year}-T${quarter}.pdf`)} data-testid="gestor-m303-pdf">
                <FilePdf className="mr-1.5" /> PDF
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-5">
              <div><div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] flex items-center gap-1"><ArrowUp size={10} /> Devengado</div><div className="font-display font-bold text-xl mt-1 tabular-nums">{fmtEur(m303.iva_devengado)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] flex items-center gap-1"><ArrowDown size={10} /> Soportado</div><div className="font-display font-bold text-xl mt-1 tabular-nums">{fmtEur(m303.iva_soportado)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Resultado</div><div className={`font-display font-bold text-xl mt-1 tabular-nums ${m303.resultado > 0 ? "text-red-400" : "text-emerald-400"}`}>{fmtEur(m303.resultado)}</div></div>
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-wider px-2 py-1 inline-block bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded">{m303.estado}</div>
          </div>
        )}
        {m111 && (
          <div className="ag-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-aqua-soft)] flex items-center gap-1.5"><Coins size={12} /> Modelo 111 · IRPF</div>
                <h3 className="font-display font-bold text-2xl tracking-tighter mt-1">Trimestre {m111.period_label}</h3>
              </div>
              <Button variant="outline" className="border-[var(--ag-border)] bg-transparent" onClick={()=>downloadAuthed(`/api/tax/m111/pdf?year=${year}&quarter=${quarter}`, `m111-${year}-T${quarter}.pdf`)} data-testid="gestor-m111-pdf">
                <FilePdf className="mr-1.5" /> PDF
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-5">
              <div><div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Colaboradores</div><div className="font-display font-bold text-xl mt-1 tabular-nums">{fmtEur(m111.retenciones_trabajo)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Alquiler 19%</div><div className="font-display font-bold text-xl mt-1 tabular-nums">{fmtEur(m111.retenciones_alquiler)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">A ingresar</div><div className="font-display font-bold text-xl mt-1 tabular-nums text-[var(--ag-gold)]">{fmtEur(m111.total_a_ingresar)}</div></div>
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-wider px-2 py-1 inline-block bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded">{m111.estado}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────── Fase 2D · KpiCard ─────────────── */
function KpiCard({ label, value, sub, tone, testid }) {
  const toneCls = tone === "aqua" ? "text-[var(--ag-aqua-soft)]"
    : tone === "gold" ? "text-[var(--ag-gold)]"
    : tone === "amber" ? "text-amber-400"
    : "text-[var(--ag-text)]";
  return (
    <div className="ag-card p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">{label}</div>
      <div className={`font-display font-black text-3xl mt-2 tabular-nums ${toneCls}`}>{value}</div>
      <div className="text-xs text-[var(--ag-text-dim)] mt-1">{sub}</div>
    </div>
  );
}

/* ─────────── Fase 2D · Grid de organizaciones (multi-org) ─────────── */
function GestorOrgsGrid({ orgs, activeOrgId, aggStats, onSwitchOrg }) {
  const activeStats = (aggStats || []).find(x => x.org.id === activeOrgId);

  // Total consolidado · basado en la org activa + centros conocidos (nombres/ciudades)
  // NOTA: agregación multi-org completa requiere iterar `switch-org` en backend.
  // Fase 2D · Mostramos el número de centros + KPI de la org activa como "vista actual".
  return (
    <section data-testid="gestor-orgs-grid">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold flex items-center gap-2">
          <Buildings weight="duotone" /> Mis centros gestionados
        </div>
        <span className="text-[11px] tabular-nums text-[var(--ag-gold)] font-bold" data-testid="gestor-orgs-count">
          {orgs.length} centros
        </span>
      </div>

      {/* KPIs consolidados honestos (Fase 2D · principio "no inventar datos") */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard
          label="Centros activos"
          value={orgs.length}
          sub="Con relación gestor-org"
          tone="gold"
          testid="gestor-agg-centros"
        />
        <KpiCard
          label="Centro seleccionado"
          value={activeStats?.org?.name || "—"}
          sub={activeStats?.org?.city || (activeStats ? "Sin ciudad" : "Selecciona un centro")}
          testid="gestor-agg-vista"
        />
        <KpiCard
          label="Ingresos vista actual"
          value={activeStats?.stats ? fmtEur(activeStats.stats.total_ingresos) : "No disponible"}
          sub="Solo del centro activo"
          tone="aqua"
          testid="gestor-agg-ingresos"
        />
        <KpiCard
          label="Servicios vista actual"
          value={activeStats?.stats?.services_count ?? "—"}
          sub="Cámbia de centro para comparar"
          testid="gestor-agg-servicios"
        />
      </div>

      {/* Lista de centros con acción "Entrar al centro" */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {orgs.map((o) => {
          const isActive = o.id === activeOrgId;
          return (
            <div
              key={o.id}
              className={`ag-card p-4 transition-colors ${isActive ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/5" : "hover:border-[var(--ag-gold)]/40"}`}
              data-testid={`gestor-org-card-${o.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {o.logo_url ? (
                    <img src={o.logo_url} alt="" className="w-11 h-11 rounded-sm object-cover shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] grid place-items-center shrink-0">
                      <Buildings size={20} weight="duotone" className="text-[var(--ag-gold)]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base tracking-tight truncate">{o.name}</div>
                    <div className="text-[11px] text-[var(--ag-text-dim)] flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {o.city && (<><MapPin size={10} /><span>{o.city}</span></>)}
                      {o.is_primary && (
                        <span className="px-1.5 py-0.5 rounded bg-[var(--ag-gold)]/15 text-[var(--ag-gold)] text-[9px] uppercase tracking-wider font-bold">
                          Principal
                        </span>
                      )}
                      {isActive && (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 text-[9px] uppercase tracking-wider font-bold inline-flex items-center gap-1">
                          <CheckCircle size={9} weight="fill" /> Activo
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {!isActive ? (
                  <Button
                    size="sm"
                    onClick={() => onSwitchOrg(o.id)}
                    className="ag-btn-gold h-8 rounded-sm text-xs"
                    data-testid={`gestor-org-switch-${o.id}`}
                  >
                    <ArrowRight size={11} className="mr-1" weight="bold" /> Entrar al centro
                  </Button>
                ) : (
                  <Link
                    to={o.slug ? `/marketplace/centros/${o.slug}` : "#"}
                    target="_blank"
                    rel="noopener"
                    className={`inline-flex items-center gap-1 px-3 h-8 rounded-sm text-xs bg-transparent border border-[var(--ag-border)] text-[var(--ag-text)] hover:border-[var(--ag-gold)]/40 hover:text-[var(--ag-gold)] ${!o.slug ? "opacity-40 pointer-events-none" : ""}`}
                    data-testid={`gestor-org-view-public-${o.id}`}
                  >
                    <Storefront size={11} /> Ver ficha pública
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
